import { notifyAuthExpired, syncAuthStateFromSession } from "@/lib/auth-client";
import { getAccessTokenOrNull, getAuthStateSnapshot } from "@/lib/auth-store";

export class GatewayAuthRequiredError extends Error {
  constructor() {
    super("La sesion de Keycloak no esta disponible");
    this.name = "GatewayAuthRequiredError";
  }
}

interface GatewaySessionRecoveryDependencies {
  readonly getAuthState: typeof getAuthStateSnapshot;
  readonly getAccessToken: typeof getAccessTokenOrNull;
  readonly syncSession: typeof syncAuthStateFromSession;
  readonly notifyExpired: typeof notifyAuthExpired;
  readonly fetch: typeof globalThis.fetch;
}

function currentAccessTokenNeedsRefresh(
  authState: ReturnType<typeof getAuthStateSnapshot>,
): boolean {
  const { accessToken, expiresAt } = authState;
  const nowInSeconds = Math.floor(Date.now() / 1000);
  return !accessToken || (expiresAt !== null && expiresAt <= nowInSeconds + 60);
}

function requestWithBearer(init: RequestInit, accessToken: string): RequestInit {
  const headers = new Headers(init.headers);
  headers.set("Accept", headers.get("Accept") ?? "application/json");
  headers.set("Authorization", `Bearer ${accessToken}`);
  return { ...init, headers };
}

const gatewaySessionRecoveryDependencies: GatewaySessionRecoveryDependencies = {
  getAuthState: getAuthStateSnapshot,
  getAccessToken: getAccessTokenOrNull,
  syncSession: syncAuthStateFromSession,
  notifyExpired: notifyAuthExpired,
  fetch: (...args) => globalThis.fetch(...args),
};

export async function fetchGatewayWithSessionRecovery(
  input: string | URL,
  init: RequestInit = {},
  dependencies = gatewaySessionRecoveryDependencies,
): Promise<Response> {
  if (currentAccessTokenNeedsRefresh(dependencies.getAuthState())) {
    await dependencies.syncSession();
  }
  let accessToken = dependencies.getAccessToken();
  if (!accessToken) {
    dependencies.notifyExpired("missing_access_token", "/api");
    throw new GatewayAuthRequiredError();
  }
  let response = await dependencies.fetch(input, requestWithBearer(init, accessToken));
  if (response.status !== 401) return response;

  const recovered = await dependencies.syncSession(-1);
  if (!recovered) {
    dependencies.notifyExpired("gateway_401", String(input), true);
    return response;
  }

  accessToken = dependencies.getAccessToken() ?? "";
  if (!accessToken) {
    dependencies.notifyExpired("gateway_401", String(input), true);
    return response;
  }

  response = await dependencies.fetch(input, requestWithBearer(init, accessToken));
  if (response.status === 401) {
    dependencies.notifyExpired("gateway_401", String(input), true);
  }
  return response;
}
